import React, { createContext, useContext, useState, useEffect } from 'react';
import { saveToLocalStorage, loadFromLocalStorage } from '../utils/localStorage';
import { generateSchedule, generateId } from '../utils/tournamentUtils';

const AppContext = createContext();

export const useAppContext = () => useContext(AppContext);

export const AppProvider = ({ children }) => {
  // App settings
  const [darkMode, setDarkMode] = useState(loadFromLocalStorage('darkMode') || false);
  const [soundEnabled, setSoundEnabled] = useState(loadFromLocalStorage('soundEnabled') !== false);
  
  // Tournament data
  const [tournament, setTournament] = useState(loadFromLocalStorage('tournament') || null);
  const [teams, setTeams] = useState(loadFromLocalStorage('teams') || []);
  const [matches, setMatches] = useState(loadFromLocalStorage('matches') || []);
  const [currentMatchId, setCurrentMatchId] = useState(loadFromLocalStorage('currentMatchId') || null);
  
  // Watch for changes and save to localStorage
  useEffect(() => {
    saveToLocalStorage('darkMode', darkMode);
  }, [darkMode]);
  
  useEffect(() => {
    saveToLocalStorage('soundEnabled', soundEnabled);
  }, [soundEnabled]);
  
  useEffect(() => {
    if (tournament) {
      saveToLocalStorage('tournament', tournament);
    }
  }, [tournament]);
  
  useEffect(() => {
    if (teams.length > 0) {
      saveToLocalStorage('teams', teams);
    }
  }, [teams]);
  
  useEffect(() => {
    if (matches.length > 0) {
      saveToLocalStorage('matches', matches);
    }
  }, [matches]);
  
  useEffect(() => {
    if (currentMatchId) {
      saveToLocalStorage('currentMatchId', currentMatchId);
    }
  }, [currentMatchId]);
  
  // Tournament setup function
  const setupTournament = (tournamentData, teamsList) => {
    setTournament(tournamentData);
    setTeams(teamsList);
    
    // Generate match schedule based on teams and format
    const generatedMatches = generateSchedule(teamsList, tournamentData);
    setMatches(generatedMatches);
    
    // Set current match to first match if available
    if (generatedMatches.length > 0) {
      setCurrentMatchId(generatedMatches[0].id);
    }
  };
  
  // Update match data
  const updateMatch = (matchId, matchData) => {
    setMatches(prevMatches => 
      prevMatches.map(match => 
        match.id === matchId ? { ...match, ...matchData } : match
      )
    );
  };
  
  // Update team data
  const updateTeam = (teamId, teamData) => {
    setTeams(prevTeams => 
      prevTeams.map(team => 
        team.id === teamId ? { ...team, ...teamData } : team
      )
    );
  };
  
  // Get current match
  const getCurrentMatch = () => {
    return matches.find(match => match.id === currentMatchId) || null;
  };
  
  // Calculate standings
  const calculateStandings = () => {
    // Create initial standings with 0 values
    const standings = teams.map(team => ({
      id: team.id,
      name: team.name,
      logo: team.logo,
      color: team.color,
      played: 0,
      won: 0,
      drawn: 0,
      lost: 0,
      goalsFor: 0,
      goalsAgainst: 0,
      goalDifference: 0,
      points: 0
    }));
    
    // Only count completed matches
    const completedMatches = matches.filter(match => match.status === 'completed');
    
    // Update standings based on match results
    completedMatches.forEach(match => {
      const homeTeamIndex = standings.findIndex(team => team.id === match.homeTeam.id);
      const awayTeamIndex = standings.findIndex(team => team.id === match.awayTeam.id);
      
      if (homeTeamIndex === -1 || awayTeamIndex === -1) return;
      
      // Update matches played
      standings[homeTeamIndex].played += 1;
      standings[awayTeamIndex].played += 1;
      
      // Update goals
      standings[homeTeamIndex].goalsFor += match.homeScore;
      standings[homeTeamIndex].goalsAgainst += match.awayScore;
      standings[awayTeamIndex].goalsFor += match.awayScore;
      standings[awayTeamIndex].goalsAgainst += match.homeScore;
      
      // Calculate goal difference
      standings[homeTeamIndex].goalDifference = 
        standings[homeTeamIndex].goalsFor - standings[homeTeamIndex].goalsAgainst;
      standings[awayTeamIndex].goalDifference = 
        standings[awayTeamIndex].goalsFor - standings[awayTeamIndex].goalsAgainst;
      
      // Update win/draw/loss and points
      if (match.homeScore > match.awayScore) {
        // Home team won
        standings[homeTeamIndex].won += 1;
        standings[homeTeamIndex].points += 3;
        standings[awayTeamIndex].lost += 1;
      } else if (match.homeScore < match.awayScore) {
        // Away team won
        standings[awayTeamIndex].won += 1;
        standings[awayTeamIndex].points += 3;
        standings[homeTeamIndex].lost += 1;
      } else {
        // Draw
        standings[homeTeamIndex].drawn += 1;
        standings[homeTeamIndex].points += 1;
        standings[awayTeamIndex].drawn += 1;
        standings[awayTeamIndex].points += 1;
      }
    });
    
    // Sort standings by points (primary) and goal difference (secondary)
    return standings.sort((a, b) => {
      if (b.points !== a.points) {
        return b.points - a.points;
      }
      return b.goalDifference - a.goalDifference;
    });
  };
  
  // Export all tournament data
  const exportData = () => {
    const data = {
      tournament,
      teams,
      matches,
      standings: calculateStandings()
    };
    
    // Convert to JSON string
    return JSON.stringify(data, null, 2);
  };
  
  // Import tournament data
  const importData = (jsonData) => {
    try {
      const data = JSON.parse(jsonData);
      
      if (data.tournament) setTournament(data.tournament);
      if (data.teams) setTeams(data.teams);
      if (data.matches) setMatches(data.matches);
      
      // Set current match if there are matches
      if (data.matches && data.matches.length > 0) {
        // Try to find a non-completed match to set as current
        const nextMatch = data.matches.find(match => match.status !== 'completed');
        setCurrentMatchId(nextMatch ? nextMatch.id : data.matches[0].id);
      }
      
      return true;
    } catch (error) {
      console.error('Error importing data:', error);
      return false;
    }
  };
  
  // Reset all data
  const resetData = () => {
    setTournament(null);
    setTeams([]);
    setMatches([]);
    setCurrentMatchId(null);
    
    // Clear localStorage
    localStorage.removeItem('tournament');
    localStorage.removeItem('teams');
    localStorage.removeItem('matches');
    localStorage.removeItem('currentMatchId');
  };
  
  // Add another game to the schedule
  const addAnotherGame = () => {
    if (!tournament || !matches || matches.length === 0) return;
    
    // Get the last match based on game number
    const lastMatch = [...matches].sort((a, b) => b.gameNumber - a.gameNumber)[0];
    
    // Calculate the next game number
    const nextGameNumber = lastMatch.gameNumber + 1;
    
    // Calculate the next start time
    const nextStartTime = lastMatch.startTime + lastMatch.duration + tournament.breakDuration;
    
    // Select field (cycle through available fields)
    const fieldIndex = (nextGameNumber - 1) % tournament.fields.length;
    const field = tournament.fields[fieldIndex];
    
    // Create the new match object
    const newMatch = {
      id: generateId(),
      homeTeam: null, // Placeholder, to be assigned later
      awayTeam: null, // Placeholder, to be assigned later
      homeScore: 0,
      awayScore: 0,
      events: [],
      startTime: nextStartTime,
      duration: tournament.matchDuration,
      status: 'scheduled',
      round: 'extra', // Indicate it's an added game
      fieldId: field.id,
      fieldName: field.name,
      gameNumber: nextGameNumber,
      nextMatchId: null // Not applicable for simple added game initially
    };
    
    // Add the new match to the state
    setMatches(prevMatches => [...prevMatches, newMatch]);
  };
  
  const value = {
    // States
    darkMode,
    setDarkMode,
    soundEnabled,
    setSoundEnabled,
    tournament,
    teams,
    matches,
    currentMatchId,
    setCurrentMatchId,
    
    // Functions
    setupTournament,
    updateMatch,
    updateTeam,
    getCurrentMatch,
    calculateStandings,
    exportData,
    importData,
    resetData,
    addAnotherGame
  };
  
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
